import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  StatusBar as RNStatusBar,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { invalidateToday } from '@/services/recoveryPlanService';
import { profileService } from '@/services/profileService';

type GoalOption =
  | 'reduce_control'
  | 'full_abstinence'
  | 'maintain_sobriety'
  | 'identify_triggers'
  | 'healthy_habits'
  | 'rebuild_relationships'
  | 'monitor_progress';

const GOAL_KEYS: GoalOption[] = [
  'reduce_control',
  'full_abstinence',
  'maintain_sobriety',
  'identify_triggers',
  'healthy_habits',
  'rebuild_relationships',
  'monitor_progress',
];

const GOAL_LABEL_KEY: Record<GoalOption, string> = {
  reduce_control: 'onboarding.goal.options.reduceControl',
  full_abstinence: 'onboarding.goal.options.fullAbstinence',
  maintain_sobriety: 'onboarding.goal.options.maintainSobriety',
  identify_triggers: 'onboarding.goal.options.identifyTriggers',
  healthy_habits: 'onboarding.goal.options.healthyHabits',
  rebuild_relationships: 'onboarding.goal.options.rebuildRelationships',
  monitor_progress: 'onboarding.goal.options.monitorProgress',
};

export default function ChangeGoalScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const { userProfile, updateProfile } = useAuth();

  const initial = (userProfile as any)?.app_goal as GoalOption | undefined;
  const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(initial ?? null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!initial);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await profileService.getProfile();
        if (cancelled) return;
        const goal = (fresh as any)?.app_goal as GoalOption | null | undefined;
        if (goal && GOAL_KEYS.includes(goal)) {
          setSelectedGoal(goal);
        }
      } catch (err) {
        console.warn('change-goal: could not refresh profile', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBack = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace('/diary');
  }, [router]);

  const handleSave = useCallback(async () => {
    if (!selectedGoal || saving) return;
    setSaving(true);
    try {
      const result = await updateProfile({ app_goal: selectedGoal } as any);
      if (result?.error) {
        throw result.error;
      }
      // PRD §5.10 — `settings_changed` (`field=goal`). Success path only.
      import('@/utils/clarityEvents')
        .then(({ logSettingsChanged }) => logSettingsChanged({ field: 'goal' }))
        .catch(() => {});
      invalidateToday();
      handleBack();
    } catch (err) {
      console.error('❌ change-goal: failed to save', err);
      Alert.alert(
        t('onboarding.goal.error') as string,
        t('onboarding.goal.errorMessage') as string,
      );
    } finally {
      setSaving(false);
    }
  }, [selectedGoal, saving, updateProfile, handleBack, t]);

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <RNStatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />

      <View style={styles.header}>
        <TouchableOpacity
          onPress={handleBack}
          style={styles.backButton}
          hitSlop={{ top: 12, right: 12, bottom: 12, left: 12 }}
          accessibilityRole="button"
          accessibilityLabel={t('common.back') as string}
        >
          <Ionicons name="chevron-back" size={24} color="#151515" />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>
          {t('changeGoal.headerTitle') as string}
        </Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.bodyTitle}>{t('changeGoal.bodyTitle') as string}</Text>

        {loading && !selectedGoal ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color="#151515" />
          </View>
        ) : null}

        <View style={styles.optionsList}>
          {GOAL_KEYS.map((key) => {
            const isSelected = selectedGoal === key;
            return (
              <TouchableOpacity
                key={key}
                style={[styles.card, isSelected && styles.cardSelected]}
                onPress={() => setSelectedGoal(key)}
                activeOpacity={0.85}
                accessibilityRole="radio"
                accessibilityState={{ selected: isSelected }}
              >
                <Text style={styles.optionText}>{t(GOAL_LABEL_KEY[key]) as string}</Text>
                <View style={[styles.radio, isSelected && styles.radioSelected]}>
                  {isSelected && <View style={styles.radioInner} />}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>

      <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, 16) + 8 }]}>
        <TouchableOpacity
          style={[
            styles.cta,
            (!selectedGoal || saving) && styles.ctaDisabled,
          ]}
          onPress={handleSave}
          disabled={!selectedGoal || saving}
          activeOpacity={0.85}
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.ctaText}>{t('changeGoal.cta') as string}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    height: 56,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
  },
  backButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerRight: {
    width: 24,
    height: 24,
  },
  headerTitle: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 14,
    textAlign: 'center',
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 24,
  },
  bodyTitle: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
  },
  optionsList: {
    gap: 8,
  },
  loadingWrap: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  card: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minHeight: 64,
  },
  cardSelected: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  optionText: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
  },
  radio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioSelected: {
    borderWidth: 3,
    borderColor: '#015480',
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#015480',
  },
  footer: {
    paddingHorizontal: 24,
    paddingTop: 16,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 0,
  },
  cta: {
    height: 56,
    borderRadius: 12,
    backgroundColor: '#151515',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaDisabled: {
    opacity: 0.5,
  },
  ctaText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 19.6,
    textAlign: 'center',
  },
});
